// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { DebtSummaryItem } from '@client/banks/components/shared/debts-list';
import type { BankDebtProductType } from '@client/banks/types/debt';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { formatAccountIbanReference } from '@client/banks/utils/account-reference';
import { formatBankDebtOutstandingAmount } from '@client/banks/utils/debt-snapshot';
import { hasBankUploadListData } from '@client/banks/utils/has-bank-upload-list-data';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import produce from 'immer';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useDebtsList = () => {
  const { t } = useTranslation();
  const { navigate } = useStepsData();
  const { formData, saveFormData, openListEditor } = useBankUploadData();

  const debts = useMemo(() => formData.debts ?? [], [formData.debts]);

  const canFinalize = useMemo(() => hasBankUploadListData(formData), [formData]);
  const isEmpty = useMemo(() => debts.length === 0, [debts.length]);

  const getProductTypeLabel = useCallback(
    (productType: BankDebtProductType) =>
      t(`aggregator.create-bank.debts.product-type.${productType}`, {
        ns: 'aggregator',
      }),
    [t],
  );

  const items = useMemo<DebtSummaryItem[]>(
    () =>
      debts.map((debt, index) => {
        const meta = [
          getProductTypeLabel(debt.productType),
          debt.currency,
          formatAccountIbanReference(debt.iban),
        ].join(' · ');

        return {
          index,
          debt,
          meta,
          pendingAmountLabel: formatBankDebtOutstandingAmount(debt),
        };
      }),
    [debts, getProductTypeLabel],
  );

  const openDebtForm = useCallback(
    (index?: number) => {
      openListEditor('debts', index);
      navigate('debts-form');
    },
    [navigate, openListEditor],
  );

  const onAdd = useCallback(() => {
    openDebtForm();
  }, [openDebtForm]);

  const onImportExcel = useCallback(() => {
    navigate('debts-import');
  }, [navigate]);

  const onEdit = useCallback(
    (index: number) => {
      openDebtForm(index);
    },
    [openDebtForm],
  );

  const onDelete = useCallback(
    (index: number) => {
      saveFormData(
        'debts',
        produce(debts, draft => {
          draft.splice(index, 1);
        }),
      );
    },
    [debts, saveFormData],
  );

  return {
    items,
    isEmpty,
    canFinalize,
    onAdd,
    onImportExcel,
    onEdit,
    onDelete,
  };
};
